// BKI (Baukosteninformationszentrum) construction cost data for Germany,
// per DIN 276 cost group (KG). Source: "BKI Costs for different cities.xlsx",
// authored by the cost team in May 2026; KG 300 for the >19 / simple cell
// corrected from 118 to 1118 per the cost team's June 2026 re-issue (a
// dropped digit in the original spreadsheet).
//
// Costs are €/m² of GIA. Two-step lookup:
//   1. Pick a baseline cell from the 3 × 3 matrix (size bracket × standard of
//      fit-out) — see BKI_BASELINE.
//   2. Multiply each KG value by the city's regional factor — see BKI_CITIES.
//
// Author notes preserved from the spreadsheet (column M, sheet 1):
//   • "Can we ask the user to specify [standard of fit-out] in the brief?
//      If not, lets use the medium values for now."
//   • "Can [building size] be considered? If not, lets use the >19 bracket
//      for now."
// → BKI_DEFAULTS encodes those two fallbacks.
//
// BKI does NOT cover KG 100 (land — needs BRW), KG 700 (design fees), or
// KG 800 (financing). Those KGs remain `null` here.

export type BkiCity =
  | "Aachen" | "Berlin" | "Bochum" | "Bonn" | "Braunschweig" | "Bremen"
  | "Bremerhaven" | "Chemnitz" | "Cologne" | "Dortmund" | "Dresden"
  | "Duisburg" | "Dusseldorf" | "Erfurt" | "Essen" | "Flensburg"
  | "Frankfurt" | "Gera" | "Hamburg" | "Hannover" | "Kaiserslautern"
  | "Kiel" | "Koblenz" | "Leipzig" | "Lübeck" | "Magdeburg" | "Mainz"
  | "Munich" | "Münster" | "Oldenburg" | "Osnabrück" | "Pinneberg"
  | "Rostock" | "Saarbrücken" | "Schwerin" | "Stuttgart" | "Trier"
  | "Weimar" | "Wolfsburg" | "Wuppertal";

export type BkiSizeBracket = "<6" | "6-19" | ">19";
export type BkiStandard = "simple" | "medium" | "high";

export type BkiKgRates = {
  /** €/m² GIA, KG 200 — site preparation / clearance */
  kg200: number;
  /** €/m² GIA, KG 300 — building construction (shell + structure) */
  kg300: number;
  /** €/m² GIA, KG 400 — technical installations (MEP) */
  kg400: number;
  /** €/m² GIA, KG 500 — external works / landscaping */
  kg500: number;
  /** €/m² GIA, KG 600 — furnishing */
  kg600: number;
};

/** Regional cost factors per city (multiplies baseline rates). */
export const BKI_CITIES: Record<BkiCity, number> = {
  Aachen: 0.926, Berlin: 1.141, Bochum: 0.912, Bonn: 0.920,
  Braunschweig: 0.726, Bremen: 0.921, Bremerhaven: 0.946,
  Chemnitz: 0.878, Cologne: 0.936, Dortmund: 0.757, Dresden: 0.911,
  Duisburg: 0.949, Dusseldorf: 0.983, Erfurt: 0.926, Essen: 0.839,
  Flensburg: 0.808, Frankfurt: 1.019, Gera: 0.906, Hamburg: 1.106,
  Hannover: 0.904, Kaiserslautern: 0.977, Kiel: 1.209, Koblenz: 0.938,
  Leipzig: 0.941, Lübeck: 0.921, Magdeburg: 0.851, Mainz: 0.938,
  Munich: 1.546, Münster: 0.863, Oldenburg: 0.815, Osnabrück: 0.760,
  Pinneberg: 0.975, Rostock: 1.001, Saarbrücken: 0.923, Schwerin: 1.119,
  Stuttgart: 1.114, Trier: 1.066, Weimar: 1.187, Wolfsburg: 0.817,
  Wuppertal: 0.832,
};

/**
 * Baseline €/m² rates per (size bracket × standard of fit-out) per KG.
 * Apply a city's regional factor to each rate to get the city-adjusted cost.
 */
export const BKI_BASELINE: Record<BkiSizeBracket, Record<BkiStandard, BkiKgRates>> = {
  "<6": {
    simple: { kg200: 24,  kg300: 1044, kg400: 212, kg500: 122, kg600: 0 },
    medium: { kg200: 118, kg300: 1492, kg400: 365, kg500: 176, kg600: 9 },
    high:   { kg200: 50,  kg300: 1829, kg400: 459, kg500: 343, kg600: 33 },
  },
  "6-19": {
    simple: { kg200: 37,  kg300: 1130, kg400: 267, kg500: 148, kg600: 4 },
    medium: { kg200: 115, kg300: 1425, kg400: 383, kg500: 244, kg600: 30 },
    high:   { kg200: 113, kg300: 1696, kg400: 439, kg500: 288, kg600: 18 },
  },
  ">19": {
    simple: { kg200: 52,  kg300: 1118, kg400: 312, kg500: 162, kg600: 3 },
    medium: { kg200: 51,  kg300: 1451, kg400: 390, kg500: 299, kg600: 7 },
    high:   { kg200: 60,  kg300: 1736, kg400: 502, kg500: 344, kg600: 44 },
  },
};

/**
 * Commercial space in a mixed-use building (e.g. retail in a podium) carries
 * different construction + technical rates than residential. Per the cost team
 * (June 2026), only KG 300 and KG 400 differ — every other KG uses the
 * residential baseline. These are flat €/m² baselines (no size/standard
 * variation); apply the city's regional factor like any other rate.
 */
export type BkiCommercialRates = {
  /** €/m² GIA, KG 300 — commercial building construction */
  kg300: number;
  /** €/m² GIA, KG 400 — commercial technical installations (MEP) */
  kg400: number;
};

export const BKI_COMMERCIAL_BASELINE: BkiCommercialRates = {
  kg300: 1912,
  kg400: 476,
};

/**
 * Sensible defaults when the user hasn't picked size / standard — taken
 * directly from the spreadsheet author's "lets use the … bracket for now"
 * recommendations.
 */
export const BKI_DEFAULTS = {
  sizeBracket: ">19" as BkiSizeBracket,
  standard: "medium" as BkiStandard,
} as const;

export const BKI_CITY_LIST: readonly BkiCity[] =
  Object.keys(BKI_CITIES).sort() as readonly BkiCity[];
